<template>
  <div class="w-full">
    <CommonHero
      image="https://res.cloudinary.com/benchmarkminerals/image/upload/c_fill,q_auto,g_auto,w_1920,f_auto/v1677682869/assets/lithium-prices/lithium-prices-background_idmgfe.jpg"
    >
      <div class="flex h-full flex-col justify-center text-white">
        <h1 class="text-2xl font-bold leading-7 md:leading-10 lg:text-4xl">
          Lithium Price Assessments
        </h1>
        <p class="max-w-[674px] pb-32 pt-6 text-xl font-medium">
          Benchmark Mineral Intelligence is the leading price reporting agency
          (PRA) for raw materials used in Lithium ion Batteries, electric
          vehicles and energy storage.
        </p>
        <CommonButton
          class="px-11 py-3 text-sm !font-semibold md:!w-fit"
          variant="primary"
          @click="$modal.open('contact')"
        >
          Contact Us
        </CommonButton>
      </div>
    </CommonHero>
    <main
      class="mx-auto w-full max-w-content-container px-small pb-big pt-10 lg:px-0"
    >
      <h2 class="mb-6 text-lg font-bold uppercase md:text-lg lg:text-2xl">
        Benchmark Lithium Price Data
      </h2>

      <div class="flex flex-col items-center gap-6 md:flex-row">
        <div
          class="hidden h-full min-w-[224px] max-w-[224px] overflow-hidden rounded-lg bg-p-500 md:block"
        >
          <img
            src="https://res.cloudinary.com/benchmarkminerals/image/upload/c_fill,q_auto,g_auto,f_auto/v1677682868/assets/lithium-prices/lithium-prices_i9kr91.jpg"
            alt="lithium-prices"
          />
        </div>
        <div class="flex flex-col gap-6">
          <p>
            Our team of expert analysts collect market data to mineral-specific,
            IOSCO-compliant methodologies in order to assess prices for lithium.
          </p>
          <p>
            Our specialist focus on the lithium ion battery supply chain and
            unrivalled network of industry contacts make Benchmark Minerals’
            Lithium Price Assessment the world’s most trusted source of lithium
            price data, regularly referenced in negotiations and increasingly
            used in supply chain contracts.
          </p>
          <p>
            Benchmark Minerals publishes 6 lithium carbonate (Li2CO3), 4 lithium
            hydroxide (LiOH) and 1 spodumene concentrate price. In addition,
            Benchmark derives global average prices for both lithium carbonate
            and lithium hydroxide as well as a lithium chemical index, weighted
            by volumes traded on the market.
          </p>
        </div>
      </div>
      <PriceAssessmentsSummaryViewer
        v-if="pricesSummaryPortal"
        class="mt-12"
        :price-summary="pricesSummaryPortal"
        :price-index="priceIndex"
        :category="category"
        :slug="slug"
      />

      <section
        class="mt-10 flex flex-col items-center justify-start gap-10 lg:flex-row"
      >
        <div class="w-full xl:w-1/2">
          <VideoPlayer
            :source="vimeoSource"
            :autoplay="false"
            data-testid="video"
          />
        </div>

        <div class="flex w-full flex-col xl:w-1/2">
          <div class="flex flex-col gap-6 lg:text-lg">
            <p>
              Benchmark Minerals’ lithium prices are collected directly from
              active market participants, by a team of expert industry analysts
              following a robust, market-specific, IOSCO-compliant price
              assessment process.
            </p>
            <p>
              If you’d like to know more about our process and products feel
              free to contact us.
            </p>
          </div>

          <PriceAssessmentsButtons
            class="mt-7 flex-row flex-wrap justify-start gap-4"
            :methodology-file-info="methodologyFileInfo"
          />
        </div>
      </section>

      <section
        class="mt-12 flex flex-col gap-y-10 rounded-lg bg-grey-100 px-6 py-10 lg:p-big xl:flex-row xl:gap-x-16"
      >
        <div class="w-full max-w-[640px]">
          <h2 class="mb-5 text-2xl font-bold lg:mb-4">
            Why Benchmark Lithium Prices?
          </h2>

          <div class="flex flex-col gap-6">
            <p>
              Benchmark Minerals’ lithium prices cover over 90% of global
              transactions.
            </p>
            <p>
              Our lithium price data is received by over 80% of producers and
              55% of consumers worldwide.
            </p>
            <p>
              We have an unparalleled network of over 200 lithium price provider
              contacts across the lithium supply chain and is the most respected
              price provider in the market Benchmark Minerals’ specialist focus
              means we can provide unrivalled insight into global pricing trends
            </p>
          </div>
        </div>

        <div class="w-full">
          <h2 class="mb-8 max-w-[450px] text-xl font-bold">
            What do Lithium Price Assessment Subscribers receive?
          </h2>

          <div class="grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-x-6 xl:my-0">
            <div
              v-for="(service, index) in services"
              :key="index"
              class="flex w-full items-center gap-x-3"
            >
              <Icon :name="service.icon" class="h-8 w-8" />
              <p class="font-semibold">
                {{ service.label }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact Form -->
      <div class="flex flex-col items-center justify-center">
        <div class="mb-3 mt-10 font-montserrat text-xl font-bold lg:text-4xl">
          Subscribe to gain access to our services
        </div>
        <ContactFormWithSideImage
          data-testid="lithium-prices-contact-modal"
          class="gap-y-8 lg:w-[894px] lg:gap-x-10"
          hide-contact-general
        />
      </div>

      <section class="mt-12 w-full">
        <h2 class="mb-6 text-2xl font-bold lg:pl-14">
          Other Product & Services
        </h2>

        <OurDivisionsSection />
      </section>
    </main>
  </div>
</template>

<script>
import CommonButton from '~/components/common/Button.vue'
import CommonHero from '~/components/common/Hero.vue'
import OurDivisionsSection from '~/components/OurDivisionsSection.vue'
import PriceAssessmentsButtons from '~/components/price-assessments/Buttons.vue'
import VideoPlayer from '~/components/video/VimeoPlayer.vue'
import ContactFormWithSideImage from '~/components/contact/ContactFormWithSideImage.vue'
import Icon from '~/components/common/Icon.vue'

import { getServices } from '~/constants/services'
import { PRICE_ASSESSMENTS_LITHIUM } from '~/constants/services/price-assessments'
import { LITHIUM, PRICE_ASSESSMENTS } from '~/constants/services/constants'

import SeoUtilsMixin from '~/mixins/SeoUtilsMixin'
import GetData from '~/mixins/price-assessments/GetData'
import GetVimeoDataMixin from '~/mixins/GetVimeoDataMixin'
import AuthenticationMixin from '~/mixins/AuthenticationMixin'
import MethodologyFileMixin from '~/mixins/MethodologyFileMixin'

definePageMeta({
  name: 'lithium-prices-page',
})

export default {
  components: {
    CommonButton,
    CommonHero,
    OurDivisionsSection,
    PriceAssessmentsButtons,
    VideoPlayer,
    ContactFormWithSideImage,
    Icon,
  },
  mixins: [
    MethodologyFileMixin,
    AuthenticationMixin,
    SeoUtilsMixin,
    GetData,
    GetVimeoDataMixin,
  ],
  setup() {
    const { getSeoInfo } = useSeo()
    const seo = getSeoInfo({
      title: 'Lithium Price Assessments',
      description:
        'Benchmark Mineral Intelligence is the leading price reporting agency (PRA) for raw materials used in Lithium ion Batteries, electric vehicles and energy storage.',
    })
    useHead(seo)
  },
  data() {
    return {
      category: LITHIUM.category,
      slug: 'lithium-prices',
      subscription: PRICE_ASSESSMENTS_LITHIUM.subscription,
      initialValues: getServices([PRICE_ASSESSMENTS_LITHIUM.checkbox]),
      methodologyFileInfo: {
        category: 'methodology-price-assessments',
        tag: LITHIUM.category,
      },
      services: [
        { label: '6 Lithium Carbonate', icon: 'Newspaper' },
        { label: 'Monthly Analysis', icon: 'services/icon-consultancy' },
        { label: '4 Lithium Hydroxide', icon: 'Newspaper' },
        { label: 'Charting Tool', icon: 'services/icon-chart-line' },
        { label: '1 Spodumene', icon: 'Newspaper' },
        { label: 'Online Database', icon: 'services/icon-presentation' },
      ],
      summary: null,
    }
  },
  watch: {
    async loggedIn(value) {
      if (value) {
        await this.getSummaryData()
        await this.setMethodologyFileInfo()
      }
    },
  },
  async mounted() {
    await this.getVimeoData(this.$config.public.benchmark101VimeoId)
    await this.getSummaryData()
    await this.setMethodologyFileInfo()
    this.$modal.setInitialFields(
      getServices([PRICE_ASSESSMENTS_LITHIUM.checkbox, PRICE_ASSESSMENTS.slug]),
    )
  },
  methods: {
    setSuccess(value) {
      this.success = value
      this.$emit('set-success', value)
    },
  },
}
</script>
